/**
 * piiMask — pure helpers that mask PII for the admin Users list.
 *
 * Default render contract (v2 admin Users page):
 *   - email   → ``x***@example.com`` (first char + asterisks + domain)
 *   - phone   → ``+** *** *** **89`` (last 2 digits visible)
 *   - name    → ``Mario R.`` (first name + initial of last word)
 *
 * Inspect Mode reveals the unmasked value (the page passes ``true`` to
 * the ``reveal`` flag). Audit logging happens outside this module — the
 * page calls ``elevatePiiInspect`` and records the activation.
 *
 * All helpers are pure + null-safe so they can render straight from a
 * potentially-incomplete API row (BE returns ``display_name | null``).
 */

export function maskEmail(email: string | null | undefined): string {
  if (!email) return "—";
  const at = email.indexOf("@");
  if (at <= 0) return email; // not an email — render as-is
  const local = email.slice(0, at);
  const domain = email.slice(at);
  const first = local[0] ?? "";
  return `${first}***${domain}`;
}

export function maskPhone(phone: string | null | undefined): string {
  if (!phone) return "—";
  // Keep only digits to count from the end reliably; preserve the leading
  // ``+`` if present.
  const hasPlus = phone.startsWith("+");
  const digits = phone.replace(/\D/g, "");
  if (digits.length < 2) return "—";
  const tail = digits.slice(-2);
  return `${hasPlus ? "+" : ""}** *** *** **${tail}`;
}

/**
 * Mask a display name as "First L." — first token (up to 12 chars) then
 * an initial of the second-or-later token. If only one token: render as-is.
 * If empty: return ``"—"``.
 */
export function maskName(name: string | null | undefined): string {
  if (!name || !name.trim()) return "—";
  const tokens = name.trim().split(/\s+/);
  if (tokens.length === 1) return tokens[0]!;
  const first = tokens[0]!;
  // L'iniziale deve essere una LETTERA. Prendendo alla cieca il primo carattere
  // dell'ultimo token, un nome come "Demo Account (rossiforniture.it)" usciva
  // come "Demo (." — si scartano i token che non cominciano per lettera e si
  // ripiega sul solo nome se non ne resta nessuno.
  const last = tokens
    .slice(1)
    .filter((t) => /^\p{L}/u.test(t))
    .pop();
  return last ? `${first} ${last[0]!.toUpperCase()}.` : first;
}

/**
 * Stable hash → 0..n-1, used to pick the avatar gradient seed.
 * Pure FNV-1a 32-bit — fast, dependency-free, collision-acceptable for
 * an aesthetic seed.
 */
export function hashSeed(input: string, modulo: number): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return h % modulo;
}

/**
 * Two-char initials from a (possibly null) display name; falls back to
 * the email's local part if no name is provided.
 */
export function initialsFor(
  displayName: string | null | undefined,
  email: string | null | undefined,
): string {
  const src = displayName?.trim() || email?.split("@")[0] || "";
  if (!src) return "··";
  const tokens = src
    .trim()
    .split(/[\s._-]+/)
    .filter(Boolean);
  if (tokens.length === 0) return "··";
  if (tokens.length === 1) return tokens[0]!.slice(0, 2).toUpperCase();
  return `${tokens[0]![0]!}${tokens[tokens.length - 1]![0]!}`.toUpperCase();
}
